import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';

import Button from './Button';
import Dialog, { DialogProps } from './Dialog';
import DialogActions from './DialogActions';
import DialogContent from './DialogContent';
import DialogTitle from './DialogTitle';
import Typography from './Typography';

const messages = defineMessages({
  cancelTitle: {
    id: 'components.v2.DialogAppointmentManagement.cancelTitle',
    defaultMessage: 'Cancel Appointment',
  },
  content: {
    id: 'components.v2.DialogAppointmentManagement.content',
    defaultMessage: 'Are you sure you want to cancel your appointment?',
  },
  actionCancel: {
    id: 'components.v2.DialogAppointmentManagement.actionCancel',
    defaultMessage: 'Back',
  },
  actionSubmit: {
    id: 'components.v2.DialogAppointmentManagement.actionSubmit',
    defaultMessage: 'Cancel',
  },
  rescheduleTitle: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleTitle',
    defaultMessage: 'Reschedule Warning',
  },
  rescheduleContentBeginning: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleContentBeginning',
    defaultMessage: 'You will need to',
  },
  rescheduleContentWarning: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleContentWarning',
    defaultMessage: 'cancel your original appointment',
  },
  rescheduleContentEnding: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleContentEnding',
    defaultMessage: 'to request a new appointment.',
  },
  rescheduleContentContinue: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleContentContinue',
    defaultMessage: 'Would you still like to continue?',
  },
  rescheduleActionProceed: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleActionProceed',
    defaultMessage: 'Yes, cancel appointment',
  },
  rescheduleActionKeep: {
    id: 'components.v2.DialogAppointmentManagement.rescheduleActionKeep',
    defaultMessage: 'No, keep original appointment',
  },
});

const styles = ({ breakpoints: { down }, spacing: { unit }, palette: { primary }, custom: { palette } }: Theme) =>
  createStyles({
    titleRoot: {
      marginBottom: unit * 3,
      background: primary.main,
    },
    actionRoot: {
      [down('xs')]: {
        gap: `${unit}px`,
        margin: `${unit}px ${unit * 3}px ${unit * 3}px`,
        flexDirection: 'column',
      },
    },
    action: {
      [down('xs')]: {
        width: '100%',
        margin: 0,
      },
    },
    colorDanger: {
      color: palette.colorDanger,
    },
  });

export interface DialogAppointmentManagementProps
  extends WithStyles<typeof styles>,
    Omit<DialogProps, 'classes' | 'onClose' | 'onSubmit'> {
  onClose: () => void;
  onSubmit: () => void;
  dialogType: 'cancel' | 'reschedule';
}

const DialogAppointmentManagement: React.FC<DialogAppointmentManagementProps> = ({
  open,
  onClose,
  onSubmit,
  classes,
  dialogType = 'cancel',
  ...rest
}) => {
  const { formatMessage } = useIntl();

  return (
    <Dialog open={open} fullWidth onClose={() => onClose()} {...rest}>
      <DialogTitle
        classes={{
          root: classes.titleRoot,
        }}
      >
        <Typography variant="bW600F17" color="white">
          {dialogType === 'cancel' ? formatMessage(messages.cancelTitle) : formatMessage(messages.rescheduleTitle)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {dialogType === 'cancel' ? <Typography variant="bW400F17">{formatMessage(messages.content)}</Typography> : null}
        {dialogType === 'reschedule' ? (
          <>
            <Typography variant="bW400F17" paragraph>
              {formatMessage(messages.rescheduleContentBeginning)}
              &nbsp;
              <Typography component="span" variant="bW600F17" className={classes.colorDanger} inline>
                {formatMessage(messages.rescheduleContentWarning)}
              </Typography>
              &nbsp;
              {formatMessage(messages.rescheduleContentEnding)}
            </Typography>
            <Typography variant="bW400F17">{formatMessage(messages.rescheduleContentContinue)}</Typography>
          </>
        ) : null}
      </DialogContent>
      <DialogActions
        classes={{
          action: classes.action,
          root: classes.actionRoot,
        }}
      >
        <Button
          data-lh-id="btn-DialogAppointmentManagement-submit"
          variant="contained"
          color="primary"
          onClick={() => onSubmit()}
        >
          {dialogType === 'cancel'
            ? formatMessage(messages.actionSubmit)
            : formatMessage(messages.rescheduleActionProceed)}
        </Button>
        <Button
          data-lh-id="btn-DialogAppointmentManagement-cancel"
          variant="outlined"
          color="primary"
          onClick={() => onClose()}
        >
          {dialogType === 'cancel'
            ? formatMessage(messages.actionCancel)
            : formatMessage(messages.rescheduleActionKeep)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(DialogAppointmentManagement);
